import { Injectable } from '@angular/core';
import { ROUTES, SETTINGS } from '../../clpbp-app.settings';
import { ConfigurationService, ConfigKey } from './configuration/configuration.service';
import { QueryParamService } from './session/shared/session/query-param-service/query-param.service';

@Injectable()
export class MixpanelService {
  private _Mixpanel = require('mixpanel-browser');
  private token: string;
  private queue: QueuedMethod[] = [];
  public distinct_id: string;
  public previousUrl: string;

  constructor(
    config: ConfigurationService,
    private queryParamService: QueryParamService) {

    config.getConfigValueByKey(ConfigKey.MixpanelToken).subscribe((token) => {
      this.token = token;
      this.init(token, mixpanel => {
        queryParamService.distinct_id.subscribe(value => {
          if (!value) {
            this.distinct_id = mixpanel.get_distinct_id();
          } else {
            this.distinct_id = value;
            mixpanel.identify(this.distinct_id);
          }
          queryParamService.i3id.subscribe(id => this.trackSuper({ 'i3 ID': id }));
          queryParamService.leadsource.subscribe(leadsource => this.trackSuper({ 'Lead Source': leadsource }));
          queryParamService.ldec.subscribe(ldec => this.trackSuper({ 'Email Campaign ID': ldec }));
          this.drainQueue();
        });
      });
    });
  }

  private drainQueue(): void {
    let func: QueuedMethod;

    while (func = this.queue.shift()) {
      const { funcName, funcArgs } = func;
      const method: Function = this[funcName];

      method.apply(this, funcArgs);
    }
  }

  private addToQueue(funcName: string, funcArgs: IArguments): void {
    this.queue.push({ funcName, funcArgs });
  }

  public init(token: string, callback?: Function): void {
    if (SETTINGS.API.ENV === 'RPRD') {
      this._Mixpanel.init(token, { loaded: callback });
    } else {
      this._Mixpanel.init(token, { debug: true, loaded: callback });
    }
  };

  public track(eventName: string, properties?: MixpanelProperties, callback?: Function): void {
    if (!this.token) {
      this.addToQueue('track', arguments);
      return;
    }

    if (properties && !properties.Product) {
      properties.Product = this.getProduct();
    }

    this._Mixpanel.track(eventName, properties, callback);
  };

  public trackSuper(properties: Object): void {
    if (!this.token) {
      this.addToQueue('trackSuper', arguments);
      return;
    }
    this._Mixpanel.register(properties);
  };

  public trackError(product: string, errorCode?: number, h1?: string, response?: string, requestTraceId?: string): void {
    if (!this.token) {
      this.addToQueue('trackError', arguments);
      return;
    }

    const errorObj = {
      Definition: 'Error Popped to User',
      'Error Code': errorCode,
      H1: h1,
      Response: response,
      Product: product,
      User: 'B',
      Experience: 'O',
      'Trace ID': requestTraceId
    };

    this._Mixpanel.track('Error Toasted', errorObj);
  };

  public alias(aliasStr: string): void {
    if (!this.token) {
      this.addToQueue('alias', arguments);
      return;
    }
    this._Mixpanel.alias(aliasStr);
  };

  public identify(identity: string): void {
    if (!this.token) {
      this.addToQueue('identify', arguments);
      return;
    }
    this._Mixpanel.identify(identity);
  };

  public peopleSet(info: {[property: string]: string|boolean|number}): void {
    if (!this.token) {
      this.addToQueue('peopleSet', arguments);
      return;
    }
    this._Mixpanel.people.set(info);
  };

  public getProduct(): string {
    const path = window.location.pathname;
    const { MORTGAGE_FULL, PL_FULL } = ROUTES;

    if (path.indexOf(MORTGAGE_FULL) > -1) {
      return 'M';
    } else if (path.indexOf(PL_FULL) > -1) {
      return 'P';
    } else {
      return 'U';
    }
  }
}

type QueuedMethod = {
  funcName: string;
  funcArgs: IArguments;
};

/**
 * NOTE: For more _standarish_ properties visit below:
 * https://confluence.loandepot.com/pages/viewpage.action?pageId=5605507
 * https://confluence.loandepot.com/pages/viewpage.action?spaceKey=WA&title=Mixpanel+Event+Name+Taxonomy
 * if you have further questions Lawrence Niell is the domain expert
 */
export type MixpanelProperties = {
  /** These are more standard Properties, nearly all events require them */
  'Product'?: 'M'|'P'|'U'| string;
  'User'?: 'B'|'A'|'P'| string;
  'Experience'?: 'E'|'O'|'U'|'S'| string;
  'Entry'?: string;
  'Definition'?: string;
  'Lead ID'?: string;
  /** There are plenty of other one-off keys that will map to Mixpanel tables */
  [key: string]: string;
};
